import { COLLECTIONS, MERCHANT } from "constants/action-types";
import { Observable } from "rxjs";
import { ActionsObservable } from "redux-observable";
import { ajax } from "lib/observable";
import siteConfig from "site-config";
import {
    GetMerchantFeaturePreferenceRequestAction,
    getMerchantFeaturePreferenceComplete,
    UpdateMerchantFeaturePreferenceRequestAction,
    updateMerchantFeaturePreferenceComplete,
} from "actions/merchant/merchant-feature-preference";
import { formatMessage } from "lib/globalize-util";
import { add as snackbarAdd } from "actions/snackbars";
import { updateableMerchantFeaturePreferenceFields } from "constants/merchant";
import me from "models/me";
import { AssortmentMarketStatus } from "components/product-collection/market-settings";
import { CollectionKeys } from "constants/product-collection";
import { shouldAutoOpenMarketSettings } from "helpers/merchant/market-settings";

export const getMerchantFeaturePreferenceEpic = (
    action$: ActionsObservable<GetMerchantFeaturePreferenceRequestAction>
) =>
    action$
        .ofType(MERCHANT.FEATURE_PREFERENCE.GET.REQUEST)
        .map(({ payload }) => payload)
        .switchMap(({ merchantId, collectionId }) => {
            let endpointUrl = `${siteConfig.goAPI}/v1/collections/merchants/${merchantId}/feature_config`;
            if (collectionId) {
                endpointUrl = `${endpointUrl}?collection_id=${collectionId}`;
            }
            return ajax(endpointUrl)
                .flatMap(({ response, status }) => {
                    if (status !== 200) {
                        throw new Error(response.message || formatMessage("Unknown fetch error"));
                    }
                    return [
                        getMerchantFeaturePreferenceComplete(response.feature_config),
                        { type: MERCHANT.AUTO_OPEN_MARKET_SETTINGS },
                    ];
                })
                .catch((error) => Observable.merge(Observable.of(getMerchantFeaturePreferenceComplete(error))));
        });

export const updateMerchantFeaturePreferenceEpic = (
    action$: ActionsObservable<UpdateMerchantFeaturePreferenceRequestAction>
) =>
    action$
        .ofType(MERCHANT.FEATURE_PREFERENCE.UPDATE.REQUEST)
        .map(({ payload }) => payload)
        .switchMap(({ merchantId, featurePreference }) => {
            // Only save accepted keys
            const updatingData = Object.keys(featurePreference).reduce((acc, key) => {
                if (updateableMerchantFeaturePreferenceFields[key]) {
                    acc[key] = featurePreference[key];
                }
                return acc;
            }, {});
            return ajax({
                method: "PUT",
                url: `${siteConfig.goAPI}/v1/collections/merchants/${merchantId}/feature_config`,
                body: {
                    feature_config: updatingData,
                    update_mask: {
                        paths: Object.keys(updatingData),
                    },
                },
            })
                .flatMap(({ response }) => {
                    return [
                        updateMerchantFeaturePreferenceComplete(response.feature_config),
                        snackbarAdd({ message: formatMessage("Merchant feature preference updated") }),
                    ];
                })
                .catch((error) =>
                    Observable.merge(
                        Observable.of(updateMerchantFeaturePreferenceComplete(error)),
                        Observable.of(snackbarAdd({ message: error.message || formatMessage("Error saving") }))
                    )
                );
        });

export const autoOpenMarketSettingsModalEpic = (action$, store) =>
    action$.ofType(MERCHANT.AUTO_OPEN_MARKET_SETTINGS).flatMap(() => {
        const state = store.getState();
        const { mutate, collection } = state;
        const currentCollectionMutate = mutate.snapshots?.get(collection?.collectionId);
        const isMarketSettingAutoDisplayEnabled = !currentCollectionMutate?.getIn(
            CollectionKeys.MERCHANT_SHARED_SETTINGS.MARKET_SETTINGS_DO_NOT_AUTO_DISPLAY
        );
        const marketStatus = collection?.driveItem?.collection_facet?.market_status;
        const hasDepartmentIdUnfilledOut = !collection?.driveItem?.collection_facet?.department_id;
        const isKindOfStatusThatShouldOpen = [
            AssortmentMarketStatus.PARTICIPATION_CONFIRMED,
            AssortmentMarketStatus.PENDING_CONFIRMATION,
            AssortmentMarketStatus.POST_MARKET_UPLOAD,
        ].includes(AssortmentMarketStatus[marketStatus]);
        const isBuyer = me.isBuyer();
        const isBrandUser = me.isBrandUser();

        const openMarketSettings = shouldAutoOpenMarketSettings({
            isMarketSettingAutoDisplayEnabled,
            isBuyer,
            hasDepartmentIdUnfilledOut,
            isBrandUser,
            isKindOfStatusThatShouldOpen,
        });

        return openMarketSettings
            ? [
                  {
                      type: COLLECTIONS.COLLECTION.UPDATE_COLLECTION_SETTINGS.OPEN_CLOSE,
                      payload: openMarketSettings,
                  },
              ]
            : [];
    });
